import { EdiObject } from "../../../../types/claims";
import { formatDisplayDate } from "../../../helpers/dataHelper";
import { EdiCardChipKind } from "./ediCardStyles";
import { getManualPaymentFields, isManualPayment } from "./record-payment/manualPayments";

export interface EdiCardChipInfo {
  label: string;
  kind: EdiCardChipKind;
}

export const getObjectTypeLabel = (objectType: string): string => {
  switch (objectType) {
    case "CLAIM":
      return "837 Claim";
    case "PAYMENT":
      return "835 Payment";
    case "CLAIM_STATUS":
      return "277 Status";
    default:
      return objectType;
  }
};

export const MANUAL_PAYMENT_CHIP: EdiCardChipInfo = {
  label: "Manual",
  kind: "default",
};

/** On an 835 a manual payment was joined to (REV-2585). */
export const MATCHED_MANUAL_CHIP: EdiCardChipInfo = {
  label: "Matched manual",
  kind: "default",
};

/** The timeline card's chip, "Manual · <payer>", so payments from different payers tell apart. */
export const getManualPaymentChip = (ediObj: EdiObject): EdiCardChipInfo => {
  const payerName = getManualPaymentFields(ediObj).payerName.trim();
  return payerName
    ? { ...MANUAL_PAYMENT_CHIP, label: `Manual · ${payerName}` }
    : MANUAL_PAYMENT_CHIP;
};

/** The card title: a manual payment reads as a Payment or a Denial, not as an 835. */
export const getEdiObjectLabel = (ediObj: EdiObject): string => {
  if (isManualPayment(ediObj)) {
    return getManualPaymentFields(ediObj).outcome === "paid"
      ? "Payment"
      : "Denial";
  }
  return getObjectTypeLabel(ediObj.objectType);
};

const TIER_CHIP_LABELS = {
  primary: "Primary",
  secondary: "Secondary",
  tertiary: "Tertiary",
};

// X12 835 CLP02 (claim status) codes: 1/19 primary, 2/20 secondary, 3/21 tertiary, 22 reversal of previous payment
const CLAIM_STATUS_CODE_CHIPS: Record<string, EdiCardChipInfo> = {
  "1": { label: "Primary", kind: "success" },
  "19": { label: "Primary", kind: "success" },
  "2": { label: "Secondary", kind: "success" },
  "20": { label: "Secondary", kind: "success" },
  "3": { label: "Tertiary", kind: "success" },
  "21": { label: "Tertiary", kind: "success" },
  "22": { label: "Reversal", kind: "warning" },
};

export const getEdiObjectChip = (ediObj: EdiObject): EdiCardChipInfo | null => {
  const obj = ediObj as Record<string, unknown>;
  if (ediObj.objectType === "CLAIM") {
    const transaction = obj.transaction as Record<string, unknown> | undefined;
    const transactionType = (
      transaction?.transactionType as string | undefined
    )?.toUpperCase();
    if (transactionType === "INST")
      return { label: "Institutional", kind: "default" };
    if (transactionType === "PROF")
      return { label: "Professional", kind: "default" };
    if (transactionType === "DENT") return { label: "Dental", kind: "default" };
    return null;
  }
  if (isManualPayment(ediObj)) {
    const { outcome, payerTier } = getManualPaymentFields(ediObj);
    return {
      label: TIER_CHIP_LABELS[payerTier],
      kind: outcome === "paid" ? "success" : "warning",
    };
  }
  if (ediObj.objectType === "PAYMENT") {
    const code = obj.claimStatusCode as string | undefined;
    return (code && CLAIM_STATUS_CODE_CHIPS[code]) || null;
  }
  return null;
};

// The EDI object's own date: payment, production, receipt or creation, whichever it carries
export const getEdiObjectTransactionDate = (ediObj: EdiObject): Date | null => {
  const transaction = (ediObj as Record<string, unknown>).transaction as
    | Record<string, unknown>
    | undefined;
  if (!transaction) return null;

  const dateStr = (transaction.paymentDate ||
    transaction.productionDate ||
    transaction.receiptDate ||
    transaction.creationDateTime) as string | undefined;

  if (!dateStr) return null;
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? null : parsed;
};

export const getEdiObjectDate = (ediObj: EdiObject): string => {
  const transaction = (ediObj as Record<string, unknown>).transaction as
    | Record<string, unknown>
    | undefined;
  const dateStr = (transaction?.paymentDate ||
    transaction?.productionDate ||
    transaction?.receiptDate ||
    transaction?.creationDateTime) as string | undefined;
  return dateStr ? formatDisplayDate(dateStr) : "-";
};

export const getEdiObjectAmounts = (
  ediObj: EdiObject,
): { billed?: number; paid?: number } => {
  if (isManualPayment(ediObj)) {
    // Stored as a decimal string; a denial has no paid amount to show
    const { outcome, amount } = getManualPaymentFields(ediObj);
    return { paid: outcome === "paid" ? Number(amount) : undefined };
  }
  const obj = ediObj as Record<string, unknown>;
  return {
    billed:
      typeof obj.chargeAmount === "number"
        ? (obj.chargeAmount as number)
        : undefined,
    paid:
      typeof obj.paymentAmount === "number"
        ? (obj.paymentAmount as number)
        : undefined,
  };
};
